import { CXPAddress } from "src/runtimeComponent/subComponent/AddressComponent";
import { Address } from "../runtimeComponent/subComponent/AddressSearchComponent";
import DataUtils from "./DataUtils";
import { LotTypeMap, PropertIdentifier, PropertIdentifierMap } from "../entities/Enums";

export const getCxpAddressString = (address?: CXPAddress ): string => {
    if (!address || !address.addressLine1) {
        return '';
    }
    let addString = '';
     if (address.addressLine2) {
        addString += address.addressLine2 + ', ';
    }

    addString += address.addressLine1;

    if (address.city) {
        addString += ', ' + address.city;
    }

    addString += ', ' + (address.province || 'ON');

    if (address.postalCode) {
        addString += ', ' + address.postalCode;
    }

    return addString;
}

export const getUnite = (address: Address, mask = false): string => {
    let unite = '';
    if (address.suiteNumber && address.unitType) {
        unite = address.unitType;
        unite += mask ? DataUtils.maskString(address.suiteNumber, 0) : " " + address.suiteNumber + ' - ';
    }
    return unite;
}

export const getManualEnterUnite = (address: Address, mask = false): string => {
    let unite = '';
    if (address.suiteNumber) {
        unite += mask ? DataUtils.maskString(address.suiteNumber, 0) : " " + address.suiteNumber + ' - ';
    }
    return unite;
}

export const getLot = (address: Address, mask = false, lang='en'): string => {
    let lot = '';
    if (address.lotType && address.lotType) {
        lot = LotTypeMap[lang.toUpperCase()][address.lotType];
        lot += mask? DataUtils.maskString(address.lotNumber, 0) : " " + address.lotNumber + ', ';
    }
    return lot;
}

export const getAddressLine = (address: Address, mask = false): string => {
    if (address.isNoStreetNoAddress) {
        return '';
    }
    const suffix = address.streetNumberSuffix?.trim()
        ? mask ? DataUtils.maskString(address.streetNumberSuffix.trim(), 0) : address.streetNumberSuffix
        : '';
    const streetNameTrimmed = address.streetName?.trim() || '';
    const streetTypeTrimmed = address.streetType?.trim() || '';
    const streetTypeToUse = (streetTypeTrimmed && streetNameTrimmed &&
        streetNameTrimmed.toLowerCase().endsWith(streetTypeTrimmed.toLowerCase()))
        ? ''
        : streetTypeTrimmed;
    let streetParts: string;
    if (address.commaAfterStreetNumber) {
        const numberBlock = [address.streetNumber, suffix]
            .filter(part => typeof part === 'string' ? part.trim() : !!part)
            .map(part => typeof part === 'string' ? part.trim() : part)
            .join(' ')
            .trim();
        const nameBlock = [streetNameTrimmed, streetTypeToUse, address.streetDirection]
            .filter(part => typeof part === 'string' ? part.trim() : !!part)
            .map(part => typeof part === 'string' ? part.trim() : part)
            .join(' ')
            .trim();
        streetParts = [numberBlock, nameBlock]
            .filter(p => p.length > 0)
            .join(', ');
    } else {
        streetParts = [
            address.streetNumber,
            suffix,
            streetNameTrimmed,
            streetTypeToUse,
            address.streetDirection,
        ]
            .filter(part => typeof part === 'string' ? part.trim() : !!part)
            .map(part => typeof part === 'string' ? part.trim() : part)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    const name = mask
        ? DataUtils.maskString(streetParts.length > 10 ? streetParts.substring(0, 10) : streetParts, 0)
        : streetParts;

    return name ? `${name}, ` : '';
}


export const getCityAndPost = (address: Address, mask = false): string => {
    const cityRaw = address.municipality ?? '';
    const provinceRaw = address.province === 'OTHER' ? (address.provinceOther ?? '') : (address.province ?? '');
    const postRaw = address.postalCode ?? '';
    const city = mask ? DataUtils.maskString(cityRaw, 0) : cityRaw;
    const postCode = mask ? DataUtils.maskString(postRaw, 0, 3) : postRaw;
    const parts = [city, provinceRaw, postCode]
        .map(p => (p || '').trim())
        .filter(p => p.length > 0);
    return parts.join(', ');
}
export const getCityAndPostAndCountry = (address: Address, mask = false): string => {
    const cityRaw = address.municipality ?? '';
    const countryRaw = address.country ?? '';
    const provinceRaw = address.province === 'OTHER' ? (address.provinceOther ?? '') : (address.province ?? '');
    const postRaw = address.postalCode ?? '';
    const city = mask ? DataUtils.maskString(cityRaw, 0) : cityRaw;
    const country = mask ? DataUtils.maskString(countryRaw, 0) : countryRaw;
    const postCode = mask ? DataUtils.maskString(postRaw, 0, 3) : postRaw;
    const parts = [city, provinceRaw, postCode, country]
        .map(p => (p || '').trim())
        .filter(p => p.length > 0);
    return parts.join(', ');
}

export const getRouteType = (address: Address, mask = false): string => {
    let rt = ''
    if (address.routeType && address.routeType) {
        rt = address.routeType + ' ' + address.routeNumber + ', ';
    }
    return rt;
}

export const getPoBox = (address: Address, mask = false): string => {
    if (!address.poBoxNumber) return '';
    return address.poBoxNumber + ', ';
}

export const getRuralRoute = (address: Address, mask = false): string => {
    if (!address.ruralRoute) return '';
    return address.ruralRoute + ', ';
}

export const getDeliveryInstallation = (address: Address, mask = false): string => {
    let di = '';
    if (address.deliveryInstallationType && address.deliveryInstallationName && address.deliveryInstallationArea) {
        di = address.deliveryInstallationType + ' ' + address.deliveryInstallationName + ' ' + address.deliveryInstallationArea + ', ';
    }
    return di;
}

export const getRouteAndDeliveryInstallation = (address: Address, mask = false): string => {
    return getRouteType(address, mask) + getDeliveryInstallation(address, mask);
}

export const getIdentifier = (address: Address, mask = false, lang='en'): string => {
    let pi = '';
    if (address.identifier && address.identifierNumber) {
        pi = PropertIdentifierMap[lang.toUpperCase()][address.identifier];
        pi += mask ? DataUtils.maskString(address.identifierNumber, 0) : ' ' + address.identifierNumber + ', ';
    }
    return pi;
}

export const getTownship = (address: Address, mask = false): string => {
    return address.township ? mask ? DataUtils.maskString(address.township, 0) : address.township + ', ' : '';
}

export const getAddress = (address?: Address | CXPAddress, mask=false, lang='en'): string =>{
    if (!address || Object.keys(address).length === 0) return '';
    if (
        address &&
        'manualAddressChecked' in address &&
        (address as Address).manualAddressChecked
    ) {
        const add = address as Address;
        return getManualEnterUnite(add, mask) + getAddressLine(add, mask) + getRuralRoute(add, mask) + getPoBox(add, mask)
            + getCityAndPostAndCountry(add, mask);
    }
    if (address.isDetailAddress) {
        const add = address as Address;
        if (add.isNoStreetNoAddress) {
            return getLot(add, mask, lang) + getUnite(add, mask) + getIdentifier(add, mask, lang) + getPoBox(add, mask)
                + getDeliveryInstallation(add, mask) + getTownship(add, mask) + getCityAndPost(add, mask);
        } else {
            return getUnite(add, mask) + getAddressLine(add, mask)
                + getRouteAndDeliveryInstallation(add, mask) + getCityAndPostAndCountry(add, mask);
        }
    }
    return getCxpAddressString(address as CXPAddress);
}

export const getAddressObject = (address?:Address)=>{
    return {
        postalCode:address.postalCode,
        streetNum: address.streetNumber,
        streetName: address.streetName,
        unitNum: address.suiteNumber,
        city: address.municipality,
        province: address.province, 
        poBox: address.poBoxNumber
    };
}

export const splitAddress=(wholeAddress?: Address, mask=false, lang='en') => {
    if (!wholeAddress) return '';
    const address = getAddress(wholeAddress, mask, lang);
    // console.log(`masked address: ${address}`);
    return address;
}
